import React from 'react'
import { Box, Card, CardContent, colors } from '@mui/material'

import {
  Bar,
  BarChart,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Rectangle,
} from 'recharts'
import { getIdentityName } from '../../../utils/identity'

function trimText(text, threshold) {
  if (text.length <= threshold) return text
  return text.substr(0, threshold).concat('...')
}

const CustomizedAxisTick = ({ x, y, stroke, payload }) => {
  return (
    <g transform={`translate(${x},${y + 20})`}>
      <text x={0} y={0} fill='#666' textAnchor='end' transform='rotate(-35)'>
        {trimText(payload.value, 10)}
      </text>
    </g>
  )
}

const BarChartView = ({ activities, activityTypes, identities }) => {
  const data = activityTypes
    .sort((at) => (at.id === 6 ? 1 : -1))
    .map((at) => {
      const val = { name: at.name, id: at.id }

      activities.allActivitiesByIdentityIds.forEach((i) => {
        const identity = identities.find((_i) => _i.id === i.identityId)
        const count =
          i.activities.find((type) => type.activityTypeId === at.id)?.count || 0

        val[getIdentityName(identity)] = count
      })

      return val
    })

  const identityNames = activities.allActivitiesByIdentityIds.map((i) => {
    const identity = identities.find((_i) => _i.id === i.identityId)
    return getIdentityName(identity)
  })

  if (!data.length) return <Box>No data</Box>

  const activitesData = data.filter((data) => data.id !== 6)
  const revenueData = data.filter((data) => data.id === 6)

  const availableColors = Object.keys(colors)

  return (
    <Card>
      <CardContent sx={{ display: 'flex' }}>
        <BarChart
          width={900}
          height={400}
          data={activitesData}
          margin={{ top: 5, right: 0, bottom: 75, left: 5 }}
        >
          <CartesianGrid strokeDasharray='10 10' />
          <XAxis
            dataKey='name'
            tick={<CustomizedAxisTick />}
            type='category'
            minTickGap={-1000}
            interval={0}
          />
          <YAxis
            yAxisId='left'
            orientation='left'
            stroke='#8884d8'
            allowDecimals={false}
          />

          <Tooltip />
          <Legend wrapperStyle={{ width: 1195, bottom: 0 }} />
          {identityNames.map((name, i) => {
            let colorIndex = i

            if (colorIndex > availableColors.length - 1) {
              colorIndex = colorIndex % availableColors.length
            }
            return (
              <>
                <Bar
                  key={i}
                  yAxisId='left'
                  dataKey={name}
                  name={name}
                  fill={colors[availableColors[colorIndex]][800]}
                />
              </>
            )
          })}
        </BarChart>
        <BarChart
          width={300}
          height={400}
          data={revenueData}
          margin={{ top: 5, right: 5, bottom: 75, left: -10 }}
        >
          <CartesianGrid strokeDasharray='10 10' />
          <XAxis
            dataKey='name'
            tick={<CustomizedAxisTick />}
            type='category'
            minTickGap={-1000}
            interval={0}
          />

          <YAxis
            yAxisId='right'
            orientation='right'
            stroke='#153208'
            allowDecimals={false}
            tickFormatter={(value) => `$${value}`}
          />
          <Tooltip />
          <Legend wrapperStyle={{ opacity: 0, bottom: 0 }} />

          {identityNames.map((name, i) => {
            let colorIndex = i

            if (colorIndex > availableColors.length - 1) {
              colorIndex = colorIndex % availableColors.length
            }
            return (
              <>
                <Bar
                  key={i}
                  yAxisId='right'
                  dataKey={name}
                  name={name}
                  fill={colors[availableColors[colorIndex]][800]}
                />
              </>
            )
          })}
        </BarChart>
      </CardContent>
    </Card>
  )
}

export default BarChartView
